/**
 * The Step 6 development-level prefill (G3-14b, decision B plus C).
 *
 * The decision of 19 August 2026, recorded in
 * docs/guide-change-spec/G3-14-DEVELOPMENT-LEVEL-DEFAULT-DECISION.md: Guide
 * writes NO automatic default. The session development level is a starting
 * value for the Step 6 selector and nothing more. Only an instructor save turns
 * it into data, and what is then recorded is an instructor act:
 * `instructor_override` with assigned_by and assigned_at (ICD 3.1.4, 8.8).
 *
 * This module does one thing: translate the session-level string into the ICD
 * 8.8 code the selector speaks. It lives in packages/shared because web and
 * mobile must offer the SAME suggestion for the same session; two copies would
 * drift into two suggestions and neither surface could say which was right.
 *
 * What it is NOT: a mapping of the session construct onto the contract. ICD
 * section 12 records that the withdrawn session-level construct
 * (sessions.trainee_tier, renamed sessions.development_level by migration 239)
 * is not the source and is not mapped, and the physical source of
 * icd.v1_development_level is still TO-CONFIRM-IN-S2-07. Nothing here claims
 * otherwise. The value is offered to an instructor, and it is the instructor's
 * confirmation, not this table, that gives a stored value its provenance.
 */

/**
 * Session development levels (migration 175's constraint, renamed by migration
 * 239) against the ICD 8.8 codes. Keys are normalised: lower case, and spaces
 * for underscores so a code-shaped value maps to itself.
 *
 * `N/A-Examiner` is deliberately absent. ICD 8.8 has three levels and no
 * fourth, so an examiner session suggests nothing and the selector opens empty
 * rather than opening on a level the contract does not have.
 */
const PREFILL_BY_SESSION_LEVEL: Record<string, string> = {
  beginner: 'beginner',
  'building competencies': 'building_competencies',
  proficient: 'proficient',
};

/**
 * The ICD 8.8 code the Step 6 selector opens with, or null for no suggestion.
 *
 * Null covers every case where there is nothing truthful to suggest: a session
 * with no development level, an examiner session, and any value the contract
 * does not carry. A null here means the selector is empty and the row saves
 * nothing, which is the Option B position for a competency nobody judged.
 */
export function developmentLevelPrefillCode(
  sessionDevelopmentLevel: string | null | undefined,
): string | null {
  if (!sessionDevelopmentLevel) return null;
  const key = sessionDevelopmentLevel.trim().toLowerCase().replace(/_/g, ' ');
  return PREFILL_BY_SESSION_LEVEL[key] ?? null;
}
